/**
 * WhatIfScenariosPage — `/reporting/whatif`.
 *
 * Multiplicative perturbations against a baseline forecast. Wired against
 * `POST /v1/reporting-ml/forecasts/what-if`.
 */
import { type JSX, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Plus, Sliders, Trash2 } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import { runWhatIf, type ForecastRunWire } from '../../lib/api/reporting';
import { DeferredNotice, PageHeader } from '../shared/StaffUI';

interface Perturbation {
  readonly stepIndex: number;
  readonly multiplier: number;
}

export function WhatIfScenariosPage(): JSX.Element {
  const [baselineRunId, setBaselineRunId] = useState('');
  const [perturbations, setPerturbations] = useState<Perturbation[]>([
    { stepIndex: 1, multiplier: 1.1 },
  ]);

  const whatIf = useMutation<ForecastRunWire, Error>({
    mutationFn: () => runWhatIf({ baselineRunId, perturbations }),
  });

  const denied =
    whatIf.isError && whatIf.error instanceof ApiError &&
    (whatIf.error.status === 401 || whatIf.error.status === 403);

  function addRow(): void {
    setPerturbations([...perturbations, { stepIndex: perturbations.length + 1, multiplier: 1 }]);
  }

  function update(idx: number, patch: Partial<Perturbation>): void {
    setPerturbations(
      perturbations.map((p, i) => (i === idx ? { ...p, ...patch } : p)),
    );
  }

  function remove(idx: number): void {
    setPerturbations(perturbations.filter((_, i) => i !== idx));
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="What-if scenarios"
        description="Perturb a baseline forecast and re-render."
      />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          whatIf.mutate();
        }}
        className="bg-white border border-border rounded-xl p-5 space-y-3"
      >
        <input
          type="text"
          value={baselineRunId}
          onChange={(e) => setBaselineRunId(e.target.value)}
          placeholder="Baseline forecast run id"
          aria-label="Baseline run id"
          className="w-full px-3 py-2 text-sm border border-border rounded-lg"
        />

        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Sliders className="w-4 h-4 text-nipost-green" /> Perturbations
            </h3>
            <button
              type="button"
              onClick={addRow}
              className="px-2 py-1 text-xs font-medium text-nipost-green hover:bg-nipost-green-light rounded inline-flex items-center gap-1"
            >
              <Plus className="w-3 h-3" /> Row
            </button>
          </div>
          <ul className="space-y-2">
            {perturbations.map((p, i) => (
              <li key={i} className="flex items-center gap-2">
                <input
                  type="number"
                  value={p.stepIndex}
                  onChange={(e) => update(i, { stepIndex: Number(e.target.value) })}
                  aria-label={`Step ${i}`}
                  className="w-24 px-2 py-1 text-xs border border-border rounded"
                />
                <input
                  type="number"
                  step="0.05"
                  value={p.multiplier}
                  onChange={(e) => update(i, { multiplier: Number(e.target.value) })}
                  aria-label={`Multiplier ${i}`}
                  className="w-32 px-2 py-1 text-xs border border-border rounded"
                />
                <button
                  type="button"
                  onClick={() => remove(i)}
                  aria-label={`Remove row ${i}`}
                  className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded inline-flex items-center"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </li>
            ))}
          </ul>
        </div>

        <button
          type="submit"
          disabled={whatIf.isPending}
          className="inline-flex items-center gap-2 px-3 py-2 text-xs font-medium text-white bg-nipost-green rounded-lg hover:bg-nipost-green/90 transition-colors disabled:opacity-50"
        >
          {whatIf.isPending ? 'Running…' : 'Run what-if'}
        </button>
      </form>

      {denied ? (
        <DeferredNotice title="Permission required">
          <code className="font-mono">Reporting/runForecast</code> required.
        </DeferredNotice>
      ) : whatIf.isError ? (
        <DeferredNotice title="What-if failed">
          {whatIf.error.message}
        </DeferredNotice>
      ) : whatIf.data ? (
        <div className="bg-white border border-border rounded-xl p-5">
          <h3 className="font-semibold text-gray-900 mb-3">
            Result · {whatIf.data.metric}
          </h3>
          <pre className="p-3 bg-gray-50 border border-border rounded-lg text-[11px] font-mono overflow-x-auto">
            {JSON.stringify(whatIf.data.forecast, null, 2)}
          </pre>
        </div>
      ) : null}
    </div>
  );
}
